import { faSpinnerThird, Icon } from "@rivet-gg/icons";
import { useQuery } from "@tanstack/react-query";
import type { PropsWithChildren } from "react";
import { DocsSheet } from "../docs-sheet";
import { Button } from "../ui/button";
import { ActorEditableState } from "./actor-editable-state";
import { useActorInspector } from "./actor-inspector-context";
import { useActorsView } from "./actors-view-context-provider";
import type { ActorId } from "./queries";

interface ActorStateTabProps {
	actorId: ActorId;
}

export function ActorStateTab({ actorId }: ActorStateTabProps) {
	const { links } = useActorsView();

	const actorQueries = useActorInspector();

	const {
		data: { isEnabled: isStateEnabled } = { isEnabled: false },
		isError,
		isLoading,
	} = useQuery(actorQueries.actorStateQueryOptions(actorId));

	if (isError) {
		return (
			<Info>
				State Preview is currently unavailable.
				<br />
				See console/logs for more details.
			</Info>
		);
	}

	if (isLoading) {
		return (
			<Info>
				<div className="flex items-center">
					<Icon icon={faSpinnerThird} className="animate-spin mr-2" />
					Loading State Editor...
				</div>
			</Info>
		);
	}

	if (!isStateEnabled) {
		return (
			<Info>
				<p>
					State Preview is not enabled for this Actor. <br /> You can
					enable it by providing a valid state constructor.
				</p>
				<Button variant="outline" asChild>
					<a
						href={links.state}
						target="_blank"
						rel="noopener noreferrer"
					>
						Documentation
					</a>
				</Button>
			</Info>
		);
	}

	return (
		<div className="flex-1 w-full min-h-0 h-full flex flex-col">
			<ActorEditableState actorId={actorId} />
		</div>
	);
}

export function Info({ children }: PropsWithChildren) {
	return (
		<div className="flex-1 flex flex-col gap-2 items-center justify-center h-full text-center max-w-lg mx-auto">
			{children}
		</div>
	);
}
